import { User, UserEntity } from "core/models/User";
import Configuration from "Configuration";
import Agent from "core/services/api";
import { Result } from "core/models";

interface UserResultObject {
    user: UserEntity;
}

/**
 * Register a new user
 * @param email         User's email - string
 * @param password      User's passord - string
 * @param projectName   Name of the user's first project - string
 */

interface RegistrationData {
    email: string;
    password: string;
    projectName: string;
    organization: {
        name: string;
        phoneNumber: string;
    };
}

const registerUser = async (
    data: RegistrationData,
): Promise<Result<User, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        ...data,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    const result = await Agent.post<UserResultObject>(
        "user/registernew/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "Unable to register user",
        };
    }

    return {
        value: new User(resultObject.user),
        error: undefined,
    };
};

/**
 * Verify a new user
 * @param verifyToken   Token a user has received or entered (verifyToken)
 * @param accessToken   Token from session context
 */

const verifyUser = async (
    verifyToken: string,
    accessToken: string,
): Promise<Result<boolean, string>> => {
    const request = {
        verifyToken,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserResultObject>(
        "user/verify/v1",
        request,
        headers,
        "site",
    );
    const { messages } = result;

    if (messages && messages.length >= 1) {
        return {
            value: undefined,
            error: "Unable to verify user",
        };
    }

    return {
        value: true,
        error: undefined,
    };
};
/**
 * Request Password Reset
 * @param email   User's email address
 */

const requestPasswordReset = async (
    email: string,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        email,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    const result = await Agent.post<UserResultObject>(
        "user/requestresetpassword/v1",
        request,
        headers,
        "site",
    );
    const { messages } = result;

    if (messages) {
        return {
            value: undefined,
            error: "Unable to request reset password",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};
/**
 * Reset Password
 * @param resetPasswordToken   The password reset token as received by email
 * @param password   The user's newly chosen password
 */

const resetPassword = async (
    resetPasswordToken: string,
    password: string,
): Promise<Result<boolean, string>> => {
    const request = {
        resetPasswordToken,
        password,
    };
    const headers = new Headers();
    const result = await Agent.post<UserResultObject>(
        "user/resetpassword/v1",
        request,
        headers,
        "site",
    );
    const { messages } = result;

    if (messages) {
        // `Messages` doesn't contain anything that allows us to tell exactly what happend
        // most common cause is probably that the link expired, however.
        return {
            value: undefined,
            error: "Unable to change password. The password reset link might have expired.",
        };
    }

    return {
        value: true,
        error: undefined,
    };
};
/**
 * Request a verification link
 * @param accessToken   Token from session context
 */

const requestVerification = async (
    accessToken: string,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);

    const result = await Agent.post<UserResultObject>(
        "user/requestverification/v1",
        request,
        headers,
        "site",
    );
    const { messages } = result;

    if (messages) {
        return {
            value: undefined,
            error: "Unable request verification",
        };
    }

    return {
        value: true,
        error: undefined,
    };
};

/**
 * List all users in a given project
 * @param projectId Selected projectId
 */

interface ListUserInProjectRequest {
    accessToken: string;
    usernameLike?: string;
    projectId: number;
    uuid?: string;
}

const listUsersInProject = async ({
    accessToken,
    usernameLike,
    projectId,
    uuid,
}: ListUserInProjectRequest): Promise<Result<User[], string>> => {
    const request = {
        projectId,
        enabledOnly: true,
        verifiedOnly: false,
        usernameLike,
        uuid,
        orderFields: [{}],
        maxCount: 500,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserEntity[]>(
        "user/list/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "Unable to list users in project",
        };
    }

    const userList: User[] = resultObject.map((user) => new User(user));

    return {
        value: userList,
        error: undefined,
    };
};

/**
 * Include a existing or new user in a given project
 * @param accessToken The logged in user's access-token, required for authentication
 * @param projectId The ID of the project you want to include the (new) user in
 * @param email The email of the user you want to add to the given project
 * @param roles The (new) users's roles within the project
 */

const includeInProject = async (
    accessToken: string,
    projectId: number,
    email: string,
    RolesEnabled: string[],
    RolesDisabled: string[],
): Promise<Result<User, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        email,
        RolesEnabled,
        RolesDisabled,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserResultObject>(
        "user/includeinproject/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "Unable to include user in project",
        };
    }
    return {
        value: new User(resultObject.user),
        error: undefined,
    };
};

/**
 * set the roles of a given user for a given project
 * @param accessToken The logged in user's access-token, required for authentication
 * @param projectId The ID of the project you want to determine a user's roles for
 * @param userId The ID user you want to set the roles for
 * @param roles The users's roles within the given project
 */

const setRoles = async (
    accessToken: string,
    projectId: number,
    userId: number,
    RolesEnabled: string[],
    RolesDisabled: string[],
): Promise<Result<User, string>> => {
    const request = {
        projectId,
        id: userId,
        RolesEnabled,
        RolesDisabled,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserEntity>(
        "user/setroles/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "Unable to set project roles for user",
        };
    }
    return {
        value: new User(resultObject),
        error: undefined,
    };
};

/**
 * remove a user from a given project
 * @param accessToken The logged in user's access-token, required for authentication
 * @param projectId The ID of the project you want to remove the user from
 * @param userId The ID user you want to remove from the project
 */

const excludeFromProject = async (
    accessToken: string,
    projectId: number,
    userId: number,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        id: userId,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserResultObject>(
        "user/excludefromproject/v1",
        request,
        headers,
        "site",
    );

    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to exclude user from project",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

/**
 * Request password reset for another user
 * @param accessToken The logged in user's access-token, required for authentication
 * @param projectId The ID of the project the user is in
 * @param userId Id of the user you want to request a password reset for
 */

const requestPasswordResetForUser = async (
    accessToken: string,
    projectId: number,
    userId: number,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        id: userId,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserResultObject>(
        "user/requestresetpasswordasuser/v1",
        request,
        headers,
        "site",
    );

    if (result.messages) {
        return {
            value: undefined,
            error: `Unable to reset user's password`,
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

/**
 * Request resend activation for a user
 * @param accessToken The logged in user's access-token, required for authentication.
 * @param projectId The ID of the project the user is in.
 * @param userId Id of the user you want to resend the activation to.
 */

const requestActivationForUser = async (
    accessToken: string,
    projectId: number,
    userId: number,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        id: userId,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UserResultObject>(
        "user/requestactivationasuser/v1",
        request,
        headers,
        "site",
    );

    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to resend user activation",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

/**
 * Activate an invited user
 * @param activateToken Token the user received by email
 * @param password The chosen password
 */
const activateUser = async (
    activateToken: string,
    password: string,
): Promise<Result<boolean, string>> => {
    const request = {
        activateToken,
        password,
    };
    const headers = new Headers();
    const result = await Agent.post<UserResultObject>(
        "user/activate/v1",
        request,
        headers,
        "site",
    );

    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to activate user",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

/**
 * Send contact form
 * @param accessToken The active user's accessToken
 * @param projectId The active project's ID
 * @param message The message the user wants to send
 */
const requestContact = async (
    accessToken: string,
    projectId: number,
    message: string,
): Promise<Result<boolean, string>> => {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        message,
        baseUrl: BASE_URL,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<Record<string, unknown>>(
        "user/requestcontact/v1",
        request,
        headers,
        "site",
    );

    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to send contactRequest",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

export {
    registerUser,
    verifyUser,
    requestPasswordReset,
    resetPassword,
    requestVerification,
    listUsersInProject,
    includeInProject,
    setRoles,
    excludeFromProject,
    requestPasswordResetForUser,
    requestActivationForUser,
    activateUser,
    requestContact,
};
